import { useState, useEffect, useRef, useMemo, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { getContextualPickerRows } from '../../contexts/inventoryFilterAxes';

/**
 * AddFilterPicker — anchored popover with type-ahead over all filter axes.
 *
 * Flat list of {axis, value} rows; type to narrow by axis label or value
 * label (substring, case-insensitive). Up/Down to navigate, Enter to
 * select, Esc to close. Click outside to dismiss.
 *
 * Portaled to document.body (React 18): the rail can live inside a
 * transformed / overflow-clipped ancestor (the witnesses grid, the inventory
 * toolbar), which would clip an in-flow absolute popover. We position it
 * `fixed` against the anchor's bounding rect instead.
 *
 * Registry-driven: defaults to the inventory FILTER_AXES via
 * getContextualPickerRows, but any caller can pass its own `getRows` (same
 * signature: (facetRows, activeFilters, ctx) => pickerRows[]) to reuse the UI
 * for a different axis set. Inventory behaviour is unchanged when getRows is
 * omitted.
 *
 * Props:
 *   documents     — current full row list (for value enumeration)
 *   docsForFacets — row set for facet counting (defaults to documents)
 *   ctx           — opaque context passed to axis getValues / matches
 *   getRows       — picker-rows fn; defaults to inventory's getContextualPickerRows
 *   activeFilters — currently-active filters array (used to mark selected rows)
 *   scopedAxis    — when opened by clicking an existing chip, limit rows to this axis
 *   onSelect      — ({axis, value}) => void
 *   onClose       — () => void
 *   anchorRef     — ref of the trigger button (for positioning)
 */
export default function AddFilterPicker({
  documents,
  docsForFacets,
  ctx,
  getRows = getContextualPickerRows,
  activeFilters = [],
  scopedAxis,
  onSelect,
  onClose,
  anchorRef,
}) {
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [pos, setPos] = useState(null);
  const inputRef = useRef(null);
  const popoverRef = useRef(null);

  // Position the portaled popover under the anchor (fixed coords).
  useLayoutEffect(() => {
    const el = anchorRef?.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: r.bottom + 6, left: r.left });
  }, [anchorRef]);

  // Contextual rows: counts reflect "what would happen if you added this
  // filter," with facet-exclusion so axes with active chips still show
  // their other values' counts honestly.
  const allRows = useMemo(() => {
    const base = docsForFacets || documents || [];
    const rows = getRows(base, activeFilters, ctx || {});
    return scopedAxis ? rows.filter(r => r.axis === scopedAxis) : rows;
  }, [getRows, docsForFacets, documents, activeFilters, ctx, scopedAxis]);

  // Type-ahead substring match on axis label OR value label.
  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter(r =>
      r.axisLabel.toLowerCase().includes(q) ||
      r.label.toLowerCase().includes(q) ||
      (r.group && r.group.toLowerCase().includes(q))
    );
  }, [allRows, query]);

  // Map of "axis|value" → true for active filters (for selected styling).
  const activeMap = useMemo(() => {
    const m = {};
    for (const f of activeFilters) m[`${f.axis}|${f.value}`] = true;
    return m;
  }, [activeFilters]);

  // Reset highlight when filtered list changes
  useEffect(() => { setHighlight(0); }, [query, allRows]);

  // Autofocus the input once it exists: the popover renders nothing until it is positioned
  // (`pos`), so an effect that ran only on mount found no input and Escape never reached it.
  useEffect(() => {
    inputRef.current?.focus();
  }, [pos]);

  // Outside click → close
  useEffect(() => {
    function handleClick(e) {
      if (popoverRef.current && popoverRef.current.contains(e.target)) return;
      if (anchorRef?.current && anchorRef.current.contains(e.target)) return;
      onClose?.();
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [anchorRef, onClose]);

  // Keyboard navigation
  function handleKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, filteredRows.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = filteredRows[highlight];
      if (row) onSelect?.({ axis: row.axis, value: row.value });
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose?.();
    }
  }

  if (!pos) return null;

  return createPortal((
    <div
      ref={popoverRef}
      className="filter-picker filter-picker--portaled"
      role="dialog"
      aria-label="Add filter"
      style={{ top: pos.top, left: pos.left }}
    >
      <div className="filter-picker-search">
        <span className="filter-picker-search-icon">🔍</span>
        <input
          ref={inputRef}
          type="text"
          className="filter-picker-input"
          placeholder={scopedAxis ? 'Change value…' : 'Filter by…'}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </div>
      <div className="filter-picker-list" role="listbox">
        {filteredRows.length === 0 && (
          <div className="filter-picker-empty">No matching filters.</div>
        )}
        {filteredRows.map((row, i) => {
          const isActive = activeMap[`${row.axis}|${row.value}`];
          const isHighlighted = i === highlight;
          return (
            <button
              key={`${row.axis}|${row.value}`}
              type="button"
              className={[
                'filter-picker-row',
                isHighlighted ? 'filter-picker-row--highlight' : '',
                isActive ? 'filter-picker-row--active' : '',
                row.count === 0 && !isActive ? 'filter-picker-row--zero' : '',
              ].filter(Boolean).join(' ')}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => onSelect?.({ axis: row.axis, value: row.value })}
              role="option"
              aria-selected={isActive}
              title={row.count === 0 && !isActive ? 'No items match this combination' : undefined}
            >
              <span className="filter-picker-row-icon">{row.axisIcon}</span>
              <span className="filter-picker-row-axis">{row.axisLabel}</span>
              <span className="filter-picker-row-sep">›</span>
              <span className="filter-picker-row-value">{row.label}</span>
              {row.group && (
                <span className="filter-picker-row-group">({row.group})</span>
              )}
              {typeof row.count === 'number' && (
                <span className="filter-picker-row-count">{row.count}</span>
              )}
              {isActive && <span className="filter-picker-row-check">✓</span>}
            </button>
          );
        })}
      </div>
    </div>
  ), document.body);
}
